import { CalendarDotsIcon, TimerIcon } from '@phosphor-icons/react';

import { Button } from '@/components/common/button';

import { DEVICE_COPY, type CommandType } from '../constants';
import { formatCountdown, stopTimerProgress } from '../details-rules';
import type { DeviceDetails } from '../details-schemas';
import { isPowerEnergised, powerControl } from '../device-rules';
import type { Device } from '../types';

import styles from './device-control-cluster.module.scss';
import { DevicePowerButton } from './device-power-button';

export type DeviceControlClusterProps = {
  details: DeviceDetails;
  device: Device;
  /** Seconds left on an active stop timer, ticked by the shared `useCountdown` hook. */
  countdownSeconds: number | null;
  /** A queue request is in flight — the disc stays disabled for the round trip (§9). */
  isQueueing?: boolean;
  onQueueCommand: (command: CommandType) => void;
  onOpenStopTimer: () => void;
  onOpenSchedule: () => void;
  onRenew: () => void;
};

/**
 * The control cluster (US-112437 §8): a dark pill carrying the Stop Timer and Schedule pills with
 * the power disc overlapping its centre.
 *
 * For an **expired** subscription the whole cluster is replaced by a single Renew Subscription
 * button — §19 removes On/Off, Stop Timer and Schedule entirely rather than disabling them.
 *
 * While a stop timer runs, the Stop Timer pill becomes the live countdown and a progress ring is
 * drawn around the disc, emptying as the stop time approaches.
 *
 * With a command queued, §9 asks for two things together — "the power icon action is disabled
 * **and** a 'Turn On Queued' tag is shown". `powerControl` already produces the disabled disc;
 * the tag renders directly beneath it so the two read as one control, matching how the listing
 * card pairs them.
 */
export function DeviceControlCluster({
  details,
  device,
  countdownSeconds,
  isQueueing = false,
  onQueueCommand,
  onOpenStopTimer,
  onOpenSchedule,
  onRenew,
}: DeviceControlClusterProps) {
  if (details.expired) {
    return (
      <div className={styles.expired}>
        <Button variant="neutral" onClick={onRenew}>
          Renew Subscription
        </Button>
      </div>
    );
  }

  const power = powerControl(device);
  const timer = details.controls.stopTimer;
  const timerRunning = timer.active && countdownSeconds != null;
  const progress = timerRunning ? stopTimerProgress(timer) : null;
  const queued = details.queuedCommand;

  return (
    <div className={styles.cluster}>
      <div className={styles.stack}>
        {/*
          The silhouette, and the filter that merges its two halves — see `.backdrop`. Rendered
          here rather than in a global sprite because it is meaningless without these styles, and
          only one cluster is ever on screen at a time.
        */}
        <svg className={styles.gooDef} aria-hidden focusable="false">
          <defs>
            <filter id="device-cluster-goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blurred" />
              {/*
                Alpha is multiplied hard and re-centred, which turns the blur's soft ramp back into
                a hard edge. The `-10` against `20` puts the cut at alpha 0.5, the point where the
                thresholded outline matches the unblurred shapes' own size — a lower cut would
                visibly fatten the pill.
              */}
              <feColorMatrix
                in="blurred"
                type="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -10"
              />
            </filter>
          </defs>
        </svg>

        <div className={styles.backdrop} aria-hidden>
          <span className={styles.backdropPill} />
          {power.kind !== 'hidden' ? <span className={styles.backdropDisc} /> : null}
        </div>

        <div className={styles.pill}>
          {/* An active timer cannot be edited — clicking it must not reopen the picker (§8). */}
          <button
            type="button"
            className={styles.action}
            disabled={!timer.enabled || timerRunning}
            onClick={onOpenStopTimer}
          >
            <TimerIcon
              size={18}
              aria-hidden
              className={timerRunning ? styles.timerIcon : undefined}
            />
            {timerRunning ? formatCountdown(countdownSeconds) : 'Stop Timer'}
          </button>

          <span className={styles.discSlot} aria-hidden />

          {/* Always available, and highlighted once a schedule exists (§8). */}
          <button
            type="button"
            className={[
              styles.action,
              details.controls.scheduleConfigured ? styles.actionActive : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onOpenSchedule}
          >
            <CalendarDotsIcon size={18} aria-hidden />
            Schedule
          </button>
        </div>

        {power.kind !== 'hidden' ? (
          <div className={styles.disc}>
            {progress != null ? (
              // Conic gradient ring: full at the start, emptying toward the stop time.
              <span
                className={styles.ring}
                style={{ '--progress': `${progress * 360}deg` } as React.CSSProperties}
                aria-hidden
              />
            ) : null}
            <DevicePowerButton
              command={power.command}
              disabled={power.kind === 'disabled' || isQueueing}
              energised={isPowerEnergised(device)}
              size="lg"
              onQueue={onQueueCommand}
            />
          </div>
        ) : null}
      </div>

      {/* §9: the tag names the queued direction and sits with the disabled disc. */}
      {queued && power.kind !== 'hidden' ? (
        <p className={styles.queuedTag}>
          <span className={styles.queuedDot} aria-hidden />
          {DEVICE_COPY.QUEUED_TAG[queued.type]}
        </p>
      ) : null}
    </div>
  );
}
